<template>
  <div class="hierarchy-sidebar column full-height">
    <!-- Building Details -->
    <template v-if="selectedItem && selectedItem.type === 'building'">
      <q-card flat bordered class="details-card">
        <q-card-section class="bg-info text-white q-pa-sm">
          <div class="row items-center no-wrap">
            <q-icon name="business" size="24px" class="q-mr-sm" />
            <div class="column">
              <div class="text-subtitle2 text-weight-bold">{{ selectedItem.label }}</div>
              <div class="text-caption">Building</div>
            </div>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section class="q-pa-md">
          <div class="text-h6 q-mb-md">Building Information</div>

          <q-input
            v-model="editForm.name"
            label="Building Name"
            outlined
            dense
            class="q-mb-md"
          />

          <q-input
            v-model="editForm.description"
            label="Description"
            type="textarea"
            outlined
            dense
            rows="3"
            class="q-mb-md"
          />

          <div class="row justify-end q-gutter-sm">
            <q-btn
              label="Save Changes"
              color="primary"
              icon="save"
              :loading="saving"
              @click="saveChanges"
            />
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section>
          <div class="text-subtitle2 q-mb-sm">Actions</div>
          <q-btn
            flat
            color="primary"
            icon="add"
            label="Add Level"
            class="full-width q-mb-sm"
            @click="emit('addLevel', selectedItem)"
          />
          <q-btn
            flat
            color="negative"
            icon="delete"
            label="Delete Building"
            class="full-width"
            @click="emit('deleteBuilding', selectedItem)"
          />
        </q-card-section>
      </q-card>
    </template>

    <!-- Level Details -->
    <template v-else-if="selectedItem && selectedItem.type === 'level'">
      <q-card flat bordered class="details-card">
        <q-card-section class="bg-primary text-white q-pa-sm">
          <div class="row items-center no-wrap">
            <q-icon name="layers" size="24px" class="q-mr-sm" />
            <div class="column">
              <div class="text-subtitle2 text-weight-bold">{{ selectedItem.label }}</div>
              <div class="text-caption">Level</div>
            </div>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section class="q-pa-md">
          <div class="text-h6 q-mb-md">Level Information</div>

          <q-input
            v-model="editForm.name"
            label="Level Name"
            outlined
            dense
            class="q-mb-md"
          />

          <q-input
            v-model.number="editForm.level_number"
            label="Level Number"
            type="number"
            outlined
            dense
            class="q-mb-md"
            hint="e.g., 0 for ground floor, 1 for first floor, -1 for basement"
          />

          <q-input
            v-model="editForm.description"
            label="Description"
            type="textarea"
            outlined
            dense
            rows="3"
            class="q-mb-md"
          />

          <div class="row justify-end q-gutter-sm">
            <q-btn
              label="Save Changes"
              color="primary"
              icon="save"
              :loading="saving"
              @click="saveChanges"
            />
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section>
          <div class="text-subtitle2 q-mb-sm">Actions</div>
          <q-btn
            flat
            color="secondary"
            icon="add"
            label="Add Space"
            class="full-width q-mb-sm"
            @click="emit('addSpace', selectedItem)"
          />
          <q-btn
            flat
            color="negative"
            icon="delete"
            label="Delete Level"
            class="full-width"
            @click="emit('deleteLevel', selectedItem)"
          />
        </q-card-section>
      </q-card>
    </template>

    <!-- Space Details -->
    <template v-else-if="selectedItem && selectedItem.type === 'space'">
      <q-card flat bordered class="details-card">
        <q-card-section class="bg-secondary text-white q-pa-sm">
          <div class="row items-center no-wrap">
            <q-icon name="meeting_room" size="24px" class="q-mr-sm" />
            <div class="column">
              <div class="text-subtitle2 text-weight-bold">{{ selectedItem.label }}</div>
              <div class="text-caption">{{ formatBrickClass(selectedItem.brickClass) }}</div>
            </div>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section class="q-pa-md">
          <div class="text-h6 q-mb-md">Space Information</div>

          <q-input
            v-model="editForm.name"
            label="Space Name"
            outlined
            dense
            class="q-mb-md"
          />

          <q-input
            v-model="editForm.brick_class"
            label="BRICK Class"
            outlined
            dense
            class="q-mb-md"
            hint="e.g., brick:Room, brick:Office, brick:Laboratory"
          />

          <q-input
            v-model.number="editForm.area"
            label="Area (m²)"
            type="number"
            outlined
            dense
            class="q-mb-md"
          />

          <q-input
            v-model="editForm.description"
            label="Description"
            type="textarea"
            outlined
            dense
            rows="3"
            class="q-mb-md"
          />

          <div class="row justify-end q-gutter-sm">
            <q-btn
              label="Save Changes"
              color="primary"
              icon="save"
              :loading="saving"
              @click="saveChanges"
            />
          </div>
        </q-card-section>

        <q-separator />

        <!-- Equipment Documentation Table -->
        <q-card-section v-if="spaceEquipment.length > 0" class="q-pa-md">
          <div class="text-h6 q-mb-md">Equipment Documentation Status</div>
          <q-table
            :rows="spaceEquipment"
            :columns="equipmentColumns"
            row-key="id"
            flat
            dense
            :rows-per-page-options="[0]"
            hide-pagination
            class="clickable-table cursor-pointer"
            @row-click="onEquipmentRowClick"
          >
            <template #body-cell-documentation="props">
              <q-td :props="props">
                <span :class="getDocumentationColor(props.row.documentationPercentage)">
                  {{ props.row.documentationPercentage }}%
                </span>
              </q-td>
            </template>
          </q-table>
          <div class="q-mt-md text-subtitle2">
            Average Completion:
            <span :class="getDocumentationColor(averageCompletion)">
              {{ averageCompletion }}%
            </span>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section>
          <div class="text-subtitle2 q-mb-sm">Actions</div>
          <q-btn
            flat
            color="accent"
            icon="precision_manufacturing"
            label="Add Equipment"
            class="full-width q-mb-sm"
            @click="emit('addEquipmentToSpace', selectedItem)"
          />
          <q-btn
            flat
            color="red"
            icon="electrical_services"
            label="Add Switchboard"
            class="full-width q-mb-sm"
            @click="emit('addSwitchboardToSpace', selectedItem)"
          />
          <q-btn
            flat
            color="primary"
            icon="info"
            label="Extra Information"
            class="full-width q-mb-sm"
            @click="emit('showSpaceSummary', selectedItem)"
          />
          <q-btn
            flat
            color="negative"
            icon="delete"
            label="Delete Space"
            class="full-width"
            @click="emit('deleteSpace', selectedItem)"
          />
        </q-card-section>
      </q-card>
    </template>

    <!-- Switchboard Details -->
    <template v-else-if="selectedItem && selectedItem.type === 'switchboard'">
      <q-card flat bordered class="details-card">
        <q-card-section class="bg-red text-white q-pa-sm">
          <div class="row items-center no-wrap">
            <q-icon name="electrical_services" size="24px" class="q-mr-sm" />
            <div class="column">
              <div class="text-subtitle2 text-weight-bold">{{ selectedItem.label }}</div>
              <div class="text-caption">Switchboard</div>
            </div>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section class="q-pa-md">
          <div class="text-h6 q-mb-md">Switchboard Information</div>

          <q-input
            v-model="editForm.name"
            label="Switchboard Name"
            outlined
            dense
            class="q-mb-md"
          />

          <q-input
            v-model="editForm.brick_class"
            label="BRICK Class"
            outlined
            dense
            class="q-mb-md"
            hint="e.g., brick:Switchboard, brick:Electrical_Panel"
          />

          <q-input
            v-model="editForm.description"
            label="Description"
            type="textarea"
            outlined
            dense
            rows="3"
            class="q-mb-md"
          />

          <div class="row justify-end q-gutter-sm">
            <q-btn
              label="Save Changes"
              color="primary"
              icon="save"
              :loading="saving"
              @click="saveChanges"
            />
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section>
          <div class="text-subtitle2 q-mb-sm">Actions</div>
          <q-btn
            flat
            color="negative"
            icon="delete"
            label="Delete Switchboard"
            class="full-width"
            @click="emit('deleteSwitchboard', selectedItem)"
          />
        </q-card-section>
      </q-card>
    </template>
  </div>
</template>

<script setup>
  import { ref, watch, computed } from 'vue';
  import { api } from '@/services/api';

  const props = defineProps({
    selectedItem: {
      type: Object,
      default: null,
    },
    getDocumentationPercentage: {
      type: Function,
      required: false,
      default: () => () => 0,
    },
  });

  const emit = defineEmits([
    'refresh',
    'addLevel',
    'addSpace',
    'addEquipmentToSpace',
    'addSwitchboardToSpace',
    'showSpaceSummary',
    'navigateToEquipment',
    'deleteBuilding',
    'deleteLevel',
    'deleteSpace',
    'deleteSwitchboard',
  ]);

  const saving = ref(false);
  const editForm = ref({
    name: '',
    description: '',
    level_number: null,
    brick_class: '',
    area: null,
  });

  // Equipment table columns
  const equipmentColumns = [
    { name: 'name', label: 'Equipment', field: 'name', align: 'left' },
    { name: 'type', label: 'Type', field: 'type', align: 'left' },
    { name: 'documentation', label: 'Documentation', field: 'documentationPercentage', align: 'right' },
  ];

  // Computed property for space equipment
  const spaceEquipment = computed(() => {
    if (!props.selectedItem || props.selectedItem.type !== 'space') {return [];}

    const children = props.selectedItem.children || [];
    return children
      .filter((child) => child.type === 'equipment' || child.type === 'switchboard')
      .map((eq) => ({
        id: eq.id,
        name: eq.label || 'Unknown',
        type: eq.brickClass || eq.data?.brickClass || 'Unknown',
        documentationPercentage: props.getDocumentationPercentage(eq),
        equipmentNode: eq,  // Store the full node for navigation
      }));
  });

  // Computed property for average completion
  const averageCompletion = computed(() => {
    if (spaceEquipment.value.length === 0) {return 0;}
    const sum = spaceEquipment.value.reduce((acc, eq) => acc + eq.documentationPercentage, 0);
    return Math.round(sum / spaceEquipment.value.length);
  });

  // Get color class based on percentage
  function getDocumentationColor(percentage) {
    if (percentage >= 70) {return 'text-green text-weight-bold';}
    if (percentage >= 30) {return 'text-orange text-weight-bold';}
    return 'text-red text-weight-bold';
  }

  // Watch for selection changes
  watch(() => props.selectedItem, (newItem) => {
    if (newItem) {
      // Populate form based on item type
      editForm.value = {
        name: newItem.label || '',
        description: newItem.data?.description || '',
        level_number: newItem.data?.level_number || null,
        brick_class: newItem.brickClass || '',
        area: newItem.data?.area || null,
      };
    }
  }, { immediate: true });

  function formatBrickClass(brickClass) {
    if (!brickClass) {return 'Space';}
    return brickClass
      .replace('brick:', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  async function saveChanges() {
    if (!props.selectedItem?.id) {return;}

    saving.value = true;
    try {
      let endpoint = '';
      let payload = {};

      switch (props.selectedItem.type) {
        case 'building':
          endpoint = `/api/buildings/${props.selectedItem.id}`;
          payload = {
            name: editForm.value.name,
            description: editForm.value.description,
          };
          break;

        case 'level':
          endpoint = `/api/levels/${props.selectedItem.id}`;
          payload = {
            name: editForm.value.name,
            level_number: editForm.value.level_number,
            description: editForm.value.description,
          };
          break;

        case 'space':
          endpoint = `/api/spaces/${props.selectedItem.id}`;
          payload = {
            name: editForm.value.name,
            brick_class: editForm.value.brick_class,
            area: editForm.value.area,
            description: editForm.value.description,
          };
          break;

        case 'switchboard':
          endpoint = `/api/equipment/${props.selectedItem.id}`;
          payload = {
            name: editForm.value.name,
            brick_class: editForm.value.brick_class,
            description: editForm.value.description,
          };
          break;
      }

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: api.getHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to save changes:', error);
        alert('Failed to save changes');
        return;
      }

      // Refresh the hierarchy
      emit('refresh');
    } catch (error) {
      console.error('Failed to save changes:', error);
      alert('Failed to save changes');
    } finally {
      saving.value = false;
    }
  }

  function onEquipmentRowClick(event, row) {
    // Emit event to navigate to the equipment in the tree
    emit('navigateToEquipment', row.equipmentNode);
  }
</script>

<style lang="scss" scoped>
.hierarchy-sidebar {
  background: #f5f5f5;
  border-left: 1px solid #e0e0e0;
  padding: 8px;
  height: 100%;
  overflow-y: auto;
}

.details-card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
}

.clickable-table :deep(tbody tr) {
  cursor: pointer;
  transition: background-color 0.2s;
}

.clickable-table :deep(tbody tr:hover) {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
